import { isValidEmailInput } from '../../domain/identityInputPolicy';
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, validateNewPassword } from '../../domain/passwordPolicy';
import { isValidSourceUrl } from '../../domain/sourceLinkPolicy';

/**
 * Lightweight client-side validators used by the form modals.
 * Keep these independent of framework state — they accept primitives
 * and return booleans / cleaned values, so they can be unit tested in
 * isolation (see `test/validation.test.ts`).
 */

export function isValidUrl(value: string | null | undefined): boolean {
	return typeof value === 'string' && isValidSourceUrl(value);
}

const ACCEPTED_TYPES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "image/png",
  "image/jpeg",
  "image/webp",
]);

const MIME_BY_EXTENSION: Record<string, string> = {
	png: "image/png",
	jpg: "image/jpeg",
	jpeg: "image/jpeg",
	webp: "image/webp",
	pdf: "application/pdf",
	doc: "application/msword",
	docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	txt: "text/plain",
};

export const MAX_ATTACHMENT_BYTES = 30 * 1024 * 1024;

export interface AttachmentValidation {
  ok: boolean;
  reason?: "type" | "size";
}

export function resolveAttachmentMime(file: { name?: string; type?: string }): string | null {
	const reported = (file.type ?? "").trim().toLowerCase();
	if (reported && reported !== 'application/octet-stream' && reported !== 'binary/octet-stream') {
		return reported === "image/jpg" ? "image/jpeg" : reported;
	}
	const extension = (file.name ?? "").split(".").pop()?.toLowerCase() ?? "";
	return MIME_BY_EXTENSION[extension] ?? null;
}

export function validateAttachment(file: { name?: string; type?: string; size?: number }): AttachmentValidation {
  const type = resolveAttachmentMime(file);
  if (!type || !ACCEPTED_TYPES.has(type)) return { ok: false, reason: "type" };
  const size = file.size ?? 0;
  if (size <= 0 || size > MAX_ATTACHMENT_BYTES) return { ok: false, reason: "size" };
  return { ok: true };
}

/**
 * Password validation rules shared by registration and password reset.
 * A longer passphrase plus a common-password blocklist avoids predictable
 * composition rules while retaining the same server-side policy.
 *
 * Server-side mirrors these rules in httpSchemas.ts (registerSchema /
 * resetPasswordSchema). Keep both sides in sync.
 */
export { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH };

export function validateEmail(email: string): string | null {
  const value = email.trim();
  if (!value) return "Please enter your email.";
  if (!isValidEmailInput(value)) {
    return "Please enter a valid email address.";
  }
  return null;
}

export function validatePassword(password: string): string | null {
  return validateNewPassword(password);
}

export function validatePasswordMatch(password: string, confirm: string): string | null {
  if (password !== confirm) return "Passwords do not match.";
  return null;
}

/**
 * Best-effort sanitization of a user-supplied file name for use in
 * an R2 object key. Strips path separators, collapses runs of unsafe
 * characters, and trims to 80 chars. Empty / whitespace-only input
 * yields "file".
 */
export function safeFileName(name: string): string {
  const cleaned = (name ?? "")
    .replace(/[\\/\0]/g, "_")
    .replace(/[^A-Za-z0-9._-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
  return cleaned || "file";
}
